import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { SubmitHandler, useForm } from "react-hook-form"
import { Modal } from "@/shared/ui/modal"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { closePaymentModal, selectIsPaymentModalOpen, selectOnPayment } from "../model"
import { bookingPayment, getBonusBalance } from "@/shared/api/aviabooking"
import { selectBooking } from "../model/selectors"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { selectAirlines, TransporterIcon } from "@/entities/aviabooking-reference"
import { ButtonLoader } from "@/shared/ui/button-loader"

const formatBonusBalance = (balance: number) => {
    const hasFraction = !Number.isInteger(balance)

    return new Intl.NumberFormat(navigator.language || 'ru-RU', {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2
    }).format(balance)
}

const FlightTimeInfo: FC<{ datetime: string }> = ({
    datetime,
}) => {
    const date = new Date(datetime)
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return (
        <span>
            {hours}:{minutes}
        </span>
    )
}

export const BookingPaymentModal = () => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectIsPaymentModalOpen)
    const onPayment = useAppSelector(selectOnPayment)
    const booking = useAppSelector(selectBooking)
    const airlinesReference = useAppSelector(selectAirlines)
    const [bonusBalance, setBonusBalance] = useState<number | null>(null)
    const [isBonusBalanceLoading, setIsBonusBalanceLoading] = useState(false)
    const [bonusBalanceError, setBonusBalanceError] = useState<string | null>(null)

    const handleClose = () => {
        dispatch(closePaymentModal())
    }

    const loadBonusBalance = useCallback(async () => {
        try {
            setIsBonusBalanceLoading(true)
            setBonusBalanceError(null)
            const response = await getBonusBalance()
            setBonusBalance(response.data.bonus_balance)
        } catch (e) {
            if (e.response?.status === 429) {
                notify.error('Баланс можно обновлять не чаще одного раза в 3 секунды')
                setBonusBalanceError('Повторите запрос через несколько секунд')
            } else {
                setBonusBalanceError('Не удалось загрузить баланс')
            }
        } finally {
            setIsBonusBalanceLoading(false)
        }
    }, [])

    useEffect(() => {
        if (!isOpen) return

        loadBonusBalance()
    }, [isOpen, loadBonusBalance])

    const onSubmit: SubmitHandler<{}> = async () => {
        try {
            const paymentResponse = await bookingPayment(booking.booking_id)
            onPayment?.()
            switch(paymentResponse.data.detail) {
            case "already_paid":
                notify.success('Билет уже оплачен')
                break
            case "paid":
                notify.success('Оплата прошла успешно!')
                break
            }
            handleClose()
        } catch (e) {
            notify.error(getErrorMessage(e.response?.data?.error_id))
        }
    }

    const getAirline = useCallback((airlineCode: string) => {
        return airlinesReference.find(airline => airline.code === airlineCode)
    }, [airlinesReference])

    const {
        handleSubmit,
        formState: {
            isSubmitting
        }
    } = useForm()

    const flightInfo = useMemo(() => {
        if (!booking?.segments?.length) return null
        
        const firstSegment = booking.segments[0]
        const lastSegment = booking.segments[booking.segments.length - 1]
        const airlines = Array.from(new Set(booking.segments.map(s => s.marketing_airline)))
        
        return {
            departure: firstSegment.dep_airport_code,
            arrival: lastSegment.arr_airport_code,
            departureTime: firstSegment.dep_date_time,
            arrivalTime: lastSegment.arr_date_time,
            airlines,
            price: booking.price_summary
        }
    }, [booking])


    return (
        <Modal
            isOpen={isOpen}
            title="Оплата билета"
            onRequestClose={handleClose}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-lg mb-3">Детали бронирования</h3>

                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            {flightInfo?.airlines.map(airlineCode => (
                                <TransporterIcon
                                    key={airlineCode}
                                    name={getAirline(airlineCode)?.name}
                                    icon={getAirline(airlineCode)?.logo}
                                />
                            ))}
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-color-primary">
                                {flightInfo?.price ? flightInfo.price.total_fare.toLocaleString() : ''} {flightInfo?.price?.currency}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                        <div>
                            <span className="font-medium">{flightInfo?.departure}</span>
                            <span className="text-gray-500 mx-2">→</span>
                            <span className="font-medium">{flightInfo?.arrival}</span>
                        </div>
                        <div className="text-gray-500">
                            №{booking.booking_id}
                        </div>
                    </div>

                    <div className="text-sm text-gray-600 mt-2">
                        <FlightTimeInfo
                            datetime={flightInfo?.departureTime || ''}
                        />
                        {' - '}
                        <FlightTimeInfo
                            datetime={flightInfo?.arrivalTime || ''}
                        />
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between gap-3">
                        <div>
                            <div className="font-medium text-sm">Баланс бонусов</div>
                            <div className="text-xs text-gray-500">Доступно для оплаты</div>
                        </div>
                        <div className="text-right">
                            {isBonusBalanceLoading && (
                                <div className="text-sm text-gray-500">Загрузка...</div>
                            )}
                            {!isBonusBalanceLoading && bonusBalanceError && (
                                <div className="text-sm text-red-500">{bonusBalanceError}</div>
                            )}
                            {!isBonusBalanceLoading && !bonusBalanceError && bonusBalance !== null && (
                                <div className="text-lg font-semibold text-color-primary">
                                    {formatBonusBalance(bonusBalance)}
                                </div>
                            )}
                            <button
                                type="button"
                                disabled={isBonusBalanceLoading}
                                onClick={() => loadBonusBalance()}
                                className="text-xs text-color-primary disabled:text-gray-400"
                            >
                                Обновить
                            </button>
                        </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <h4 className="font-medium text-sm mb-2">Пассажиры:</h4>
                        {booking.travellers.map(traveller => (
                            <div key={traveller.id} className="text-sm text-gray-600">
                                {traveller.last_name} {traveller.first_name} ({traveller.type === 'ADT' ? 'Взрослый' : 'Ребенок'})
                            </div>
                        ))}
                    </div>
                </div>
                <div className="w-full flex gap-3">
                    <button onClick={handleClose} className="form_cancel_button" type="button">
                        Отменить
                    </button>
                    <button disabled={isSubmitting} className="form_handle_button inline-flex items-center justify-center gap-2" type="submit">
                        Оплатить
                        {isSubmitting && <ButtonLoader />}
                    </button>
                </div>
            </form>
        </Modal>
    )
}
